import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTwitter, 
  faFacebook, 
  faInstagram, 
  faLinkedin, 
  faGithub
} from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faPhone, faLocationDot } from '@fortawesome/free-solid-svg-icons';

const FooterContainer = styled.footer`
  background-color: #151515;
  color: #fff;
  padding: 80px 0 30px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -50px;
    left: 0;
    width: 100%;
    height: 50px;
    background-color: #151515;
    clip-path: polygon(0 100%, 100% 100%, 50% 0);
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
`;

const FooterSection = styled.div`
  margin-bottom: 30px;
`;

const FooterTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 20px;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 40px;
    height: 2px;
    background-color: #fff;
  }
`;

const FooterText = styled.p`
  margin-bottom: 20px;
  line-height: 1.6;
  color: #aaa;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: #fff;
  font-size: 1.2rem;
  transition: all 0.3s;
  
  &:hover {
    background-color: #fff;
    color: #151515;
    transform: translateY(-3px);
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 12px;
  
  a {
    color: #aaa;
    text-decoration: none;
    transition: color 0.3s;
    display: flex;
    align-items: center;
    
    svg {
      margin-right: 10px;
      font-size: 0.9rem;
    }
    
    &:hover {
      color: #fff;
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  
  svg {
    margin-right: 15px;
    margin-top: 5px;
    color: rgba(255, 255, 255, 0.6);
  }
  
  p {
    color: #aaa;
    margin: 0;
    line-height: 1.6;
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 30px;
  margin-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #aaa;
  font-size: 0.9rem;
  
  span {
    color: #fff;
  }
`;

const ButtonLink = styled(Link)`
  display: inline-block;
  padding: 10px 20px;
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
  text-decoration: none;
  border-radius: 4px;
  margin-top: 15px;
  transition: all 0.3s;
  
  &:hover {
    background-color: #fff;
    color: #151515;
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <FooterTitle>About Me</FooterTitle>
          <FooterText>
            I'm an IT professional with over five years of experience as a Web Master and Creative Frontend Developer. 
            My passion for digital literacy drives me to stay updated in web design, backend development, and SEO.
          </FooterText>
          <SocialIcons>
            <SocialIcon 
              href="#" 
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -5, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FontAwesomeIcon icon={faTwitter} />
            </SocialIcon>
            <SocialIcon 
              href="#" 
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -5, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FontAwesomeIcon icon={faFacebook} />
            </SocialIcon>
            <SocialIcon 
              href="#" 
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -5, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FontAwesomeIcon icon={faInstagram} />
            </SocialIcon>
            <SocialIcon 
              href="#" 
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -5, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FontAwesomeIcon icon={faLinkedin} />
            </SocialIcon>
            <SocialIcon 
              href="#" 
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -5, scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FontAwesomeIcon icon={faGithub} />
            </SocialIcon>
          </SocialIcons>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle>Quick Links</FooterTitle>
          <FooterLinks>
            <FooterLink>
              <Link to="/">Home</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/about">About</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services">Services</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/blog">Blog</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/contact">Contact</Link>
            </FooterLink>
          </FooterLinks>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle>Services</FooterTitle>
          <FooterLinks>
            <FooterLink>
              <Link to="/services">Web Development</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services">AI Content Writing</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services">Brand Strategy</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services">Digital Marketing</Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services">IT Consulting</Link>
            </FooterLink>
          </FooterLinks>
        </FooterSection>
        
        <FooterSection>
          <FooterTitle>Contact Info</FooterTitle>
          <ContactItem>
            <FontAwesomeIcon icon={faLocationDot} />
            <p>Istanbul, Turkey</p>
          </ContactItem>
          <ContactItem>
            <FontAwesomeIcon icon={faPhone} />
            <p>+90 123 456 7890</p>
          </ContactItem>
          <ContactItem>
            <FontAwesomeIcon icon={faEnvelope} />
            <p>contact@dogukanyaylaci.dev</p>
          </ContactItem>
          <ButtonLink to="/contact">Get in Touch</ButtonLink>
        </FooterSection>
      </FooterContent>
      
      <div style={{ padding: '0 20px' }}>
        <Copyright>
          © {currentYear} <span>Dogukan Yaylaci</span>. All Rights Reserved.
        </Copyright>
      </div>
    </FooterContainer>
  );
};

export default Footer; 